import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  useMediaQuery,
  useTheme,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  LinearProgress,
  Alert,
  Tab,
  Tabs,
  InputAdornment,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Send,
  TrendingUp,
  Favorite,
  Share,
  Download,
  Layers,
  MoreVert,
  ContentCopy,
  Preview,
  Lightbulb,
  Bolt,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Code,
  FormatQuote,
  Link as LinkIcon,
  Title,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
  AutoAwesome,
  Shield,
  Refresh,
} from '@mui/icons-material';
import { templateApi, TemplateApiError } from '../../services/templateApi';
import { useAuth } from '../../context/AuthContext';
import RichTextEditor from './RichTextEditor';
import { analyzeSpamScore } from './SpamDetector';
import { generateEmailWithAI, rewriteToAvoidSpam } from './AIEmailWriter';
import AIEmailWriterCard from './AIEmailWriterCard';

const TEMPLATE_CACHE_PREFIX = 'emailStudio:templates:v1';
const TEMPLATE_CACHE_TTL = 5 * 60 * 1000;
const TEMPLATE_CACHE_LIMIT = 250;

const getTemplateKey = (template = {}) => template?.id || template?.template_id || '';

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toTimestamp = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTemplateMetrics = (template = {}) => {
  const uses = toNumber(template.uses);
  const opens = toNumber(template.opens ?? template.totalOpens ?? template.total_opens);
  const clicks = toNumber(template.clicks ?? template.totalClicks ?? template.total_clicks);
  const conversions = toNumber(template.conversions);
  const explicitOpenRate = template.openRate ?? template.open_rate;
  const explicitClickRate = template.clickRate ?? template.click_rate;
  const explicitConversionRate = template.conversionRate ?? template.conversion_rate;
  const openRate = explicitOpenRate != null
    ? toNumber(explicitOpenRate)
    : (uses > 0 ? (opens / uses) * 100 : 0);
  const clickRate = explicitClickRate != null
    ? toNumber(explicitClickRate)
    : (uses > 0 ? (clicks / uses) * 100 : 0);
  const conversionRate = explicitConversionRate != null
    ? toNumber(explicitConversionRate)
    : (clicks > 0 ? (conversions / clicks) * 100 : (uses > 0 ? (conversions / uses) * 100 : 0));

  return {
    uses,
    opens,
    clicks,
    conversions,
    openRate,
    clickRate,
    conversionRate,
    favorite: Boolean(template.isFavorite ?? template.is_favorite),
    createdAt: toTimestamp(template.createdAt ?? template.created_at),
    lastUsed: toTimestamp(template.lastUsed ?? template.last_used ?? template.updatedAt ?? template.updated_at),
  };
};

/**
 * Professional Template Manager Component
 * Advanced template library with analytics, preview, and professional features
 */
const TemplateManager = ({
  templates = [],
  onCreateTemplate = () => {},
  onEditTemplate = () => {},
  onDeleteTemplate = () => {},
  onUseTemplate = () => {},
  onDuplicateTemplate = () => {},
  stats = {},
  mergeTags = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { token, user } = useAuth();
  const templateCacheReadyRef = useRef(false);
  const templateCacheMetaRef = useRef(0);
  const templateCacheKey = useMemo(() => {
    const baseId = user?.id || user?.email || 'anon';
    const safeToken = token ? String(token).replace(/[^a-z0-9]/gi, '').slice(0, 24) : 'anon';
    return `${TEMPLATE_CACHE_PREFIX}:${baseId}:${safeToken}`;
  }, [token, user?.email, user?.id]);
  const loadTemplateCache = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(templateCacheKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.templates)) {
        return null;
      }
      return parsed;
    } catch (error) {
      console.warn('Failed to read template cache', error);
      return null;
    }
  }, [templateCacheKey]);
  const saveTemplateCache = useCallback(
    (templateList) => {
      if (typeof window === 'undefined') {
        return;
      }
      try {
        const payload = {
          timestamp: Date.now(),
          templates: (templateList || []).slice(0, TEMPLATE_CACHE_LIMIT),
        };
        window.localStorage.setItem(templateCacheKey, JSON.stringify(payload));
        templateCacheMetaRef.current = payload.timestamp;
      } catch (error) {
        console.warn('Failed to persist template cache', error);
      }
    },
    [templateCacheKey]
  );

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [sortBy, setSortBy] = useState('recent');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mergeTagsGroup, setMergeTagsGroup] = useState('Contact');
  const [dbTemplates, setDbTemplates] = useState([]);
  const bodyRef = useRef(null);
  
  const [templateForm, setTemplateForm] = useState({
    id: '',
    name: '',
    subject: '',
    body: '',
    category: 'general',
    description: '',
    tags: [],
    openRate: 0,
    clickRate: 0,
    conversionRate: 0,
  });

  // Load templates from database when component mounts or user logs in
  useEffect(() => {
    let cancelled = false;

    if (!token || !user) {
      templateCacheReadyRef.current = false;
      templateCacheMetaRef.current = 0;
      setIsLoading(false);
      setDbTemplates([]);
      return;
    }

    const cached = loadTemplateCache();
    if (cached?.templates) {
      setDbTemplates(cached.templates);
    }
    if (cached?.timestamp) {
      templateCacheMetaRef.current = cached.timestamp;
    }
    templateCacheReadyRef.current = true;

    const cacheFresh = Boolean(
      cached?.templates?.length && cached?.timestamp && Date.now() - cached.timestamp < TEMPLATE_CACHE_TTL
    );

    if (cacheFresh) {
      setIsLoading(false);
      return;
    }

    const hadCachedTemplates = Boolean(cached?.templates?.length);

    const loadTemplates = async (showSpinner) => {
      if (showSpinner) {
        setIsLoading(true);
      }
      try {
        const response = await templateApi.listTemplates({}, token);
        if (cancelled) {
          return;
        }
        const fetchedTemplates = response?.templates || [];
        setDbTemplates(fetchedTemplates);
        saveTemplateCache(fetchedTemplates);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load templates:', error);
          if (!cached?.templates) {
            setDbTemplates([]);
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadTemplates(!hadCachedTemplates);

    return () => {
      cancelled = true;
    };
  }, [token, user, loadTemplateCache, saveTemplateCache, templateCacheKey]);

  useEffect(() => {
    if (!templateCacheReadyRef.current || !token || !user) {
      return;
    }
    saveTemplateCache(dbTemplates);
  }, [dbTemplates, saveTemplateCache, token, user]);

  const allTemplates = useMemo(() => {
    const seen = new Set();
    return [...templates, ...dbTemplates].filter((template) => {
      const key = getTemplateKey(template);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [templates, dbTemplates]);

  const templateMetrics = useMemo(() => {
    const metrics = {};
    allTemplates.forEach((template) => {
      const key = getTemplateKey(template);
      if (!key) {
        return;
      }
      metrics[key] = getTemplateMetrics(template);
    });
    return metrics;
  }, [allTemplates]);

  // Filter templates - combine props templates with database templates
  const filteredTemplates = useMemo(() => {
    let list = [...allTemplates];

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      list = list.filter((t) => {
        const text = `${t.name || ''} ${t.subject || ''} ${t.description || ''} ${(t.tags || []).join(' ')}`.toLowerCase();
        return text.includes(query);
      });
    }

    // Category filter
    if (categoryFilter !== 'all') {
      list = list.filter((t) => (t.category || 'general') === categoryFilter);
    }

    // Tab filter
    if (filterTab === 1) {
      // Favorites
      list = list.filter((t) => templateMetrics[getTemplateKey(t)]?.favorite);
    } else if (filterTab === 2) {
      // Recently used
      list = list
        .sort((a, b) => (templateMetrics[getTemplateKey(b)]?.lastUsed || 0) - (templateMetrics[getTemplateKey(a)]?.lastUsed || 0))
        .slice(0, 10);
    } else if (filterTab === 3) {
      // Top performers
      list = list
        .sort((a, b) => (templateMetrics[getTemplateKey(b)]?.openRate || 0) - (templateMetrics[getTemplateKey(a)]?.openRate || 0))
        .slice(0, 10);
    }

    // Sort
    if (sortBy === 'name') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'performance') {
      list.sort((a, b) => (templateMetrics[getTemplateKey(b)]?.openRate || 0) - (templateMetrics[getTemplateKey(a)]?.openRate || 0));
    } else if (sortBy === 'uses') {
      list.sort((a, b) => (templateMetrics[getTemplateKey(b)]?.uses || 0) - (templateMetrics[getTemplateKey(a)]?.uses || 0));
    } else {
      // recent
      list.sort((a, b) => (templateMetrics[getTemplateKey(b)]?.createdAt || 0) - (templateMetrics[getTemplateKey(a)]?.createdAt || 0));
    }

    return list;
  }, [allTemplates, searchQuery, filterTab, sortBy, categoryFilter, templateMetrics]);

  const handleOpenTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm(template);
    setPreviewOpen(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm(template);
    setEditDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      setSnackbar({ open: true, message: 'Template name, subject, and body are required', severity: 'error' });
      return;
    }

    if (!token || !user) {
      setSnackbar({ open: true, message: 'Please login to save templates', severity: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      const response = await templateApi.saveTemplate(templateForm, token);
      
      setSnackbar({ 
        open: true, 
        message: templateForm.id ? 'Template updated successfully' : 'Template saved successfully',
        severity: 'success'
      });

      // Update database templates list with saved template
      setDbTemplates((prev) => {
        const exists = prev.find((t) => (t.template_id || t.id) === (response.template.template_id || response.template.id));
        if (exists) {
          return prev.map((t) => (t.template_id || t.id) === (response.template.template_id || response.template.id) ? response.template : t);
        }
        return [...prev, response.template];
      });

      // Call the parent handler to update local state
      if (templateForm.id) {
        onEditTemplate(response.template, { skipRemote: true });
      } else {
        onCreateTemplate(response.template, { skipRemote: true });
      }

      setEditDialogOpen(false);
      setTemplateForm({
        id: '',
        name: '',
        subject: '',
        body: '',
        category: 'general',
        description: '',
        tags: [],
      });
    } catch (error) {
      const message = error instanceof TemplateApiError 
        ? error.message 
        : 'Failed to save template';
      setSnackbar({ open: true, message, severity: 'error' });
      console.error('Template save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const applyTemplateFormat = (format) => {
    const textarea = bodyRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateForm.body || '';
    const selectedText = text.substring(start, end) || 'text';

    let before = '', after = '';
    switch (format) {
      case 'bold':
        before = '**';
        after = '**';
        break;
      case 'italic':
        before = '*';
        after = '*';
        break;
      case 'underline':
        before = '<u>';
        after = '</u>';
        break;
      case 'code':
        before = '```';
        after = '```';
        break;
      case 'quote':
        before = '> ';
        break;
      case 'link':
        before = '[';
        after = '](url)';
        break;
      case 'heading':
        before = '## ';
        break;
      case 'bullet':
        before = '- ';
        break;
      case 'number':
        before = '1. ';
        break;
      case 'clear':
        setTemplateForm((prev) => ({ ...prev, body: '' }));
        return;
      default:
        return;
    }

    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    setTemplateForm((prev) => ({ ...prev, body: newText }));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
    }, 0);
  };

  const insertMergeTag = (tag) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setTemplateForm((prev) => ({
        ...prev,
        body: (prev.body || '') + tag,
      }));
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateForm.body || '';
    const newText = text.substring(0, start) + tag + text.substring(end);
    setTemplateForm((prev) => ({ ...prev, body: newText }));
    setTimeout(() => {
      textarea.focus();
      const pos = start + tag.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleToggleFavorite = async (template) => {
    const templateId = getTemplateKey(template);
    const nextFavorite = !Boolean(template.isFavorite ?? template.is_favorite);
    const updated = { ...template, isFavorite: nextFavorite };

    setDbTemplates((prev) => prev.map((item) => (
      getTemplateKey(item) === templateId
        ? { ...item, isFavorite: nextFavorite }
        : item
    )));
    onEditTemplate(updated, { skipRemote: true });

    if (!token || !user || !templateId) {
      return;
    }

    try {
      await templateApi.toggleFavorite(templateId, nextFavorite, token);
    } catch (error) {
      setDbTemplates((prev) => prev.map((item) => (
        getTemplateKey(item) === templateId
          ? { ...item, isFavorite: !nextFavorite }
          : item
      )));
      onEditTemplate({ ...template, isFavorite: !nextFavorite }, { skipRemote: true });
      setSnackbar({
        open: true,
        message: error instanceof TemplateApiError ? error.message : 'Failed to update favorite',
        severity: 'error',
      });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!token || !user) {
      setSnackbar({ open: true, message: 'Please login to delete templates', severity: 'error' });
      return;
    }

    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await templateApi.deleteTemplate(templateId, token);
      
      // Remove from database templates
      setDbTemplates((prev) => prev.filter((t) => (t.template_id || t.id) !== templateId));
      
      setSnackbar({ 
        open: true, 
        message: 'Template deleted successfully',
        severity: 'success'
      });

      // Call the parent handler to update local state
      onDeleteTemplate(templateId, { skipRemote: true });
      setSelectedTemplate(null);
    } catch (error) {
      const message = error instanceof TemplateApiError 
        ? error.message 
        : 'Failed to delete template';
      setSnackbar({ open: true, message, severity: 'error' });
      console.error('Template delete error:', error);
    }
  };

  const handleDuplicate = (template) => {
    const copy = {
      ...template,
      id: `tmpl-${Date.now()}`,
      name: `${template.name || 'Template'} (Copy)`,
    };
    onCreateTemplate(copy);
    onDuplicateTemplate(copy);
  };

  const categories = ['general', 'promotional', 'newsletter', 'transactional', 'welcome', 'followup', 're-engagement'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* ====== HEADER ====== */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          borderRadius: isMobile ? 1 : 2,
          borderLeft: '4px solid #667eea',
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Layers sx={{ fontSize: 28, color: '#667eea' }} />
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800}>
                  Template Library Pro
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Design, preview, and manage reusable email templates with advanced analytics
              </Typography>
            </Box>
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setTemplateForm({
                  id: '',
                  name: '',
                  subject: '',
                  body: '',
                  category: 'general',
                  description: '',
                  tags: [],
                });
                setEditDialogOpen(true);
              }}
              fullWidth={isMobile}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {isMobile ? 'New' : 'Create Template'}
            </Button>
          </Stack>

          {/* Stats */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#f5f5f5', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Templates</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.totalTemplates === 'number' ? stats.totalTemplates : allTemplates.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#e3f2fd', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Avg Open Rate</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.avgOpenRate === 'number'
                    ? Math.round(stats.avgOpenRate)
                    : allTemplates.length > 0
                      ? Math.round(
                          allTemplates.reduce((sum, t) => sum + (templateMetrics[getTemplateKey(t)]?.openRate || 0), 0) /
                            allTemplates.length
                        )
                      : 0}
                  %
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#e8f5e9', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Uses</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.totalUses === 'number'
                    ? stats.totalUses
                    : allTemplates.reduce((sum, t) => sum + (templateMetrics[getTemplateKey(t)]?.uses || 0), 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#fff3e0', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Favorites</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.favorites === 'number'
                    ? stats.favorites
                    : allTemplates.filter((t) => templateMetrics[getTemplateKey(t)]?.favorite).length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ====== TOOLBAR ====== */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search templates, tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          fullWidth={isMobile}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          select
          size="small"
          label="Sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          sx={{ minWidth: 150 }}
          SelectProps={{ native: true }}
        >
          <option value="recent">Most Recent</option>
          <option value="name">Name (A-Z)</option>
          <option value="performance">Top Performance</option>
          <option value="uses">Most Used</option>
        </TextField>
        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 160 }}
          SelectProps={{ native: true }}
        >
          <option value="all">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </TextField>
      </Stack>

      {/* ====== TABS ====== */}
      <Tabs
        value={filterTab}
        onChange={(e, newValue) => setFilterTab(newValue)}
        sx={{ borderBottom: '1px solid #eee', overflow: 'auto' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="All" icon={<Layers />} iconPosition="start" />
        <Tab label="Favorites" icon={<Favorite />} iconPosition="start" />
        <Tab label="Recently Used" icon={<TrendingUp />} iconPosition="start" />
        <Tab label="Top Performers" icon={<Bolt />} iconPosition="start" />
      </Tabs>

      {/* ====== TEMPLATES GRID ====== */}
      {isLoading && filteredTemplates.length === 0 ? (
        <Grid container spacing={2}>
          {Array.from({ length: isMobile ? 2 : 6 }).map((_, index) => (
            <Grid item xs={12} sm={isMobile ? 12 : 6} md={4} key={`template-skeleton-${index}`}>
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)', background: 'rgba(255,255,255,0.9)' }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Skeleton variant="text" width="60%" height={28} />
                    <Skeleton variant="text" width="80%" height={18} />
                    <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                    <Stack direction="row" spacing={1}>
                      <Skeleton variant="rounded" width={60} height={24} />
                      <Skeleton variant="rounded" width={60} height={24} />
                    </Stack>
                    <LinearProgress sx={{ height: 6, borderRadius: 999 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredTemplates.length === 0 ? (
        <Alert
          severity="info"
          icon={<Lightbulb />}
          sx={{
            background: 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0.05) 100%)',
          }}
        >
          <Typography fontWeight={700}>No templates found</Typography>
          <Typography variant="caption">Create a new template to get started</Typography>
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredTemplates.map((template) => {
            const templateId = getTemplateKey(template);
            const metrics = templateMetrics[templateId] || getTemplateMetrics(template);
            const isFavorite = metrics.favorite;

            return (
              <Grid item xs={12} sm={isMobile ? 12 : 6} md={4} key={templateId}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    border: isFavorite ? '2px solid #667eea' : '1px solid #eee',
                    backgroundColor: isFavorite ? 'rgba(102,126,234,0.05)' : '#fff',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  {/* Header with Badge */}
                  <Box sx={{
                    p: 1.5,
                    background: isFavorite ? 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)' : '#f9f9f9',
                    borderBottom: '1px solid #eee',
                    position: 'relative',
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box sx={{ flex: 1, pr: 1 }}>
                        <Typography fontWeight={700} variant="body2" noWrap>
                          {template.name || 'Untitled Template'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {template.subject || 'No subject'}
                        </Typography>
                      </Box>
                      <Tooltip title={isFavorite ? 'Remove favorite' : 'Add favorite'}>
                        <IconButton size="small" onClick={() => handleToggleFavorite(template)}>
                          <Favorite sx={{ fontSize: 20, color: isFavorite ? '#667eea' : '#ccc' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {template.category && (
                        <Chip
                          size="small"
                          label={template.category}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                      {(template.tags || []).slice(0, 2).map((tag) => (
                        <Chip key={tag} size="small" label={tag} variant="filled" sx={{ fontSize: '0.7rem' }} />
                      ))}
                    </Stack>
                  </Box>

                  {/* Analytics */}
                  <Box sx={{ p: 1.5, flex: 1 }}>
                    <Typography fontWeight={700} variant="body2" mb={1}>
                      Performance
                    </Typography>
                    <Grid container spacing={1} mb={1.5}>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center', background: '#e3f2fd' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Open Rate
                          </Typography>
                          <Typography fontWeight={700} sx={{ color: '#2196F3' }}>
                            {metrics.openRate || 0}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center', background: '#e8f5e9' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Click Rate
                          </Typography>
                          <Typography fontWeight={700} sx={{ color: '#4CAF50' }}>
                            {metrics.clickRate || 0}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" fontWeight={700}>
                            Engagement
                          </Typography>
                          <Typography variant="caption">
                            {metrics.openRate || 0}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.openRate || 0}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1} sx={{ fontSize: '0.85rem' }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Uses:</Typography>
                        <Typography fontWeight={700}>{metrics.uses || 0}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Total Opens:</Typography>
                        <Typography fontWeight={700}>{metrics.opens || 0}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Total Clicks:</Typography>
                        <Typography fontWeight={700}>{metrics.clicks || 0}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Conversions:</Typography>
                        <Typography fontWeight={700} sx={{ color: '#4CAF50' }}>
                          {metrics.conversions || 0}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Actions */}
                  <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: '1px solid #eee' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Preview />}
                      onClick={() => handleOpenTemplate(template)}
                      fullWidth
                    >
                      Preview
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Send />}
                      onClick={() => onUseTemplate(template)}
                      fullWidth
                    >
                      Use
                    </Button>
                    <Tooltip title="More options">
                      <IconButton size="small">
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {/* Quick Actions */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      p: 1,
                      borderTop: '1px solid #eee',
                      bgcolor: '#fafafa',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(template)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small">
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share">
                      <IconButton size="small">
                        <Share fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(templateId)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ====== PREVIEW DIALOG ====== */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTemplate?.name || 'Template Preview'}</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography fontWeight={700} variant="body2" mb={1}>
                  Subject
                </Typography>
                <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                  <Typography>{selectedTemplate.subject}</Typography>
                </Paper>
              </Box>
              <Box>
                <Typography fontWeight={700} variant="body2" mb={1}>
                  Preview
                </Typography>
                <Paper sx={{ p: 2, background: '#fff', border: '1px solid #ddd', minHeight: 300 }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedTemplate.body}</Typography>
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => {
              onUseTemplate(selectedTemplate);
              setPreviewOpen(false);
            }}
          >
            Use This Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====== EDIT DIALOG ====== */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[20],
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a237e 0%, #283593 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            py: 2.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {templateForm.id ? <Edit /> : <Add />}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {templateForm.id ? 'Edit Template' : 'Create New Template'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Design professional email templates with rich formatting
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Template Name"
                fullWidth
                size="small"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g., Welcome Email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Category"
                fullWidth
                size="small"
                value={templateForm.category}
                onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                SelectProps={{ native: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email Subject"
                fullWidth
                size="small"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="e.g., Welcome to our platform!"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            
            {/* AI Email Writer & Spam Detection - Reusable Component */}
            <Grid item xs={12}>
              <AIEmailWriterCard
                subject={templateForm.subject || ''}
                body={templateForm.body || ''}
                onUpdate={({ subject, body }) => {
                  setTemplateForm(prev => ({
                    ...prev,
                    ...(subject !== undefined && { subject }),
                    ...(body !== undefined && { body })
                  }));
                }}
                mergeTags={mergeTags}
                buttonText="Generate Template"
                isMobile={isMobile}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Brief description of this template's purpose"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tags"
                fullWidth
                size="small"
                value={(templateForm.tags || []).join(', ')}
                onChange={(e) =>
                  setTemplateForm({
                    ...templateForm,
                    tags: e.target.value
                      .split(',')
                      .map((tag) => tag.trim())
                      .filter(Boolean),
                  })
                }
                placeholder="e.g., onboarding, promo, follow-up"
                helperText="Comma-separated tags for easy filtering"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600}
                  color="text.primary" 
                  sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <Lightbulb fontSize="small" color="primary" />
                  Email Body
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                  Create beautiful emails with rich formatting, images, videos, and dynamic merge tags
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: theme.shadows[2],
                  }}
                >
                  <RichTextEditor
                    value={templateForm.body || ''}
                    onChange={(html) => setTemplateForm({ ...templateForm, body: html })}
                    placeholder="Design your email template with formatting, images, and merge tags..."
                    height={400}
                    minHeight={300}
                    mergeTags={mergeTags}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 3, 
            borderTop: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.02)'
              : 'rgba(0, 0, 0, 0.01)',
          }}
        >
          <Button 
            onClick={() => setEditDialogOpen(false)}
            sx={{ px: 3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : (templateForm.id ? <Edit /> : <Add />)}
            sx={{ 
              px: 4,
              py: 1,
              borderRadius: 1.5,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #5568d3 0%, #63408b 100%)'
                  : 'linear-gradient(135deg, #5568d3 0%, #63408b 100%)',
              }
            }}
          >
            {isSaving ? 'Saving...' : (templateForm.id ? 'Save Changes' : 'Create Template')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateManager;
